// TODO: build function that transforms state into donation form action payload
export type State = {
  focusedPane: Panes
  designations: Designation[]
  frequency: Frequency
  draftFrequency?: DraftFrequency
}

// String to make text inputs work nice by default
export type Amount = string

// Calculated totals should be a number: a blank value will be 0 not ""
export type Total = number

export type Panes = "left" | "right"

export type Fund = {
  description: string
  id: string
  name: string
  slug: string
}

export type Designation = {
  fundId: string
  amount: Amount
}

export type ConfigAPIResponse = {
  data: {
    id: string
    type: "DonationFormConfig"
    attributes: {
      accept_apple_pay: boolean
      card_giving_level: "all_cards" | "no_credit_cards"
      default_amount_cents: number
      default_to_recurring: boolean
      fee_coverage_allowed: boolean
      funds: Fund[]
      minimum_donation_amount_cents: number
      default_schedules: {
        monthly: { starting: DateStampString }
        weekly: { starting: DateStampString }
        every_other_week: { starting: DateStampString }
        twice_monthly: { starting: DateStampString }
      }
      stripe_publishable_key: string
      stripe_account_identifier: string
    }
    relationships: {
      default_fund: {
        data: {
          id: string
          type: string
        }
      }
    }
    links: {
      self: string
    }
  }
}

export type Config = {
  accept_apple_pay: boolean
  card_giving_level: "all_cards" | "no_credit_cards"
  default_amount_cents: number
  default_to_recurring: boolean
  fee_coverage_allowed: boolean
  minimum_donation_amount_cents: number
  default_schedules: {
    monthly: { starting: DateStampString }
    weekly: { starting: DateStampString }
    every_other_week: { starting: DateStampString }
    twice_monthly: { starting: DateStampString }
  }
  funds: Fund[]
  default_fund: Fund
  stripe_publishable_key: string
  stripe_account_identifier: string
}

export const WEEK_DAY_LABELS = {
  0: "Sunday",
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
} as const satisfies Record<number, string>

export type WeekDayNumber = keyof typeof WEEK_DAY_LABELS

// Intentionally 1-28
export const MONTH_DAY_LABELS = {
  1: "1st",
  2: "2nd",
  3: "3rd",
  4: "4th",
  5: "5th",
  6: "6th",
  7: "7th",
  8: "8th",
  9: "9th",
  10: "10th",
  11: "11th",
  12: "12th",
  13: "13th",
  14: "14th",
  15: "15th",
  16: "16th",
  17: "17th",
  18: "18th",
  19: "19th",
  20: "20th",
  21: "21st",
  22: "22nd",
  23: "23rd",
  24: "24th",
  25: "25th",
  26: "26th",
  27: "27th",
  28: "28th",
} as const satisfies Record<number, string>

export type MonthDayNumber = keyof typeof MONTH_DAY_LABELS

export const TWICE_MONTHLY_LABELS = {
  "1-15": "1st & 15th",
  "6-20": "6th & 20th",
  "11-25": "11th & 25th",
} as const

export type TwiceMonthlyDays = keyof typeof TWICE_MONTHLY_LABELS

// Something like "2025-01-30"
export type DateStampString = string

// Shape accepted by the backend
export type Schedule =
  // monthly
  | { day_in_month: { day: MonthDayNumber } }
  // weekly
  | { weekday: { weekday: WeekDayNumber } }
  // every_other_week
  | { biweekly: { start_after: DateStampString; weekday: WeekDayNumber } }
  // twice_monthly
  | {
      union: [
        { day_in_month: { day: MonthDayNumber } },
        { day_in_month: { day: MonthDayNumber } },
      ]
    }

export type FrequencyType = Extract<Frequency["type"], string>

export type MonthlyFrequency = {
  type: "monthly"
  starting: DateStampString
  day: MonthDayNumber
}

export type WeeklyFrequency = {
  type: "weekly"
  starting: DateStampString
  weekday: WeekDayNumber
}

export type EveryOtherWeekFrequency = {
  type: "every_other_week"
  starting: DateStampString
  weekday: WeekDayNumber
}

export type TwiceMonthlyFrequency = {
  type: "twice_monthly"
  starting: DateStampString
  days: TwiceMonthlyDays
}

export type Frequency =
  | { type: "once" }
  | MonthlyFrequency
  | WeeklyFrequency
  | EveryOtherWeekFrequency
  | TwiceMonthlyFrequency

// One-time isn't a type of frequency that can be edited
export type DraftFrequency = Exclude<Frequency, { type: "once" }>
export type RepeatableFrequency = DraftFrequency

export type StripeCustomerSessionClientSecret = string
